// Coordinates live in the listing's own `latitude` / `longitude` columns.
// Older listings carried the seller's pin as a "\nlat:…,lng:…" suffix inside
// the address text; that format is only *read* now (see coordsOf) until the
// backend backfill moves it into the columns (docs/BACKEND_TASKS.md, B2).
const COORDS_SUFFIX_REGEX = /\nlat:([\d.]+),lng:([\d.]+)$/;

/** The listing's exact point: the coordinate columns, else a legacy address suffix. */
export function coordsOf(p: {
  latitude?: unknown;
  longitude?: unknown;
  address?: string | null;
}): { lat: number; lng: number } | null {
  const lat = Number(p?.latitude);
  const lng = Number(p?.longitude);
  if (lat && lng) return { lat, lng };
  const legacy = decodeAddressWithCoords(p?.address ?? "");
  return legacy.lat && legacy.lng ? { lat: legacy.lat, lng: legacy.lng } : null;
}

/** Address text without a legacy coordinates suffix — what create/edit send. */
export function cleanAddress(raw: string | null | undefined): string {
  return (raw ?? "").replace(COORDS_SUFFIX_REGEX, "").trim();
}

export function decodeAddressWithCoords(raw: string): {
  address: string;
  lat: number | null;
  lng: number | null;
} {
  if (!raw) {
    return { address: "", lat: null, lng: null };
  }
  const match = raw.match(COORDS_SUFFIX_REGEX);
  if (!match) return { address: raw, lat: null, lng: null };

  const address = raw.replace(COORDS_SUFFIX_REGEX, "").trim();
  const lat = parseFloat(match[1]);
  const lng = parseFloat(match[2]);
  // Older listings were saved with "lat:0,lng:0" when no pin was placed —
  // that's "no location", not a point in the Gulf of Guinea.
  if (!lat || !lng) return { address, lat: null, lng: null };

  return { address, lat, lng };
}

export function displayAddress(raw: string): string {
  return raw.replace(COORDS_SUFFIX_REGEX, "").trim();
}

export function hasCoords(raw: string): boolean {
  return COORDS_SUFFIX_REGEX.test(raw);
}
